import React, { useState, useEffect, useRef, useMemo } from "react";
import { connect } from "react-redux";
import axios from "axios";
import { serverLink } from "../../../resources/url";
import DataLoader from "../../common/dataLoader/dataLoader";
import ApexChart from "react-apexcharts";
import { useReactToPrint } from "react-to-print";

// ─── Colour Palette & Base Config ────────────────────────────────────────────

const C = {
    primary:  '#206bc4',
    success:  '#2fb344',
    warning:  '#f76707',
    danger:   '#d63939',
    info:     '#4299e1',
    purple:   '#ae3ec9',
    teal:     '#20c997',
    orange:   '#fd7e14',
    palette:  [
        '#206bc4','#2fb344','#ae3ec9','#f76707',
        '#d63939','#4299e1','#f59f00','#20c997',
        '#fd7e14','#6c757d','#0dcaf0','#198754',
    ],
};

const BASE = {
    toolbar: { show: false },
    fontFamily: 'inherit',
    animations: { enabled: true, speed: 700 },
};

const pct = (n, d) => (d === 0 ? '0.0%' : `${((n / d) * 100).toFixed(1)}%`);

// ─── Analytics Tabs ───────────────────────────────────────────────────────────

const TABS = [
    { key: 'level',       label: 'By Level'       },
    { key: 'department',  label: 'By Department'  },
    { key: 'programme',   label: 'By Programme'   },
    { key: 'gender',      label: 'By Gender'      },
    { key: 'study_type',  label: 'By Study Type'  },
    { key: 'year',        label: 'By Adm. Year'   },
];

// ─── KPI Icon ─────────────────────────────────────────────────────────────────

function KpiIcon({ type, color }) {
    const s = {
        width: 18, height: 18, viewBox: '0 0 24 24', strokeWidth: 2,
        stroke: color, fill: 'none', strokeLinecap: 'round', strokeLinejoin: 'round',
    };
    const icons = {
        students: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>,
        dept:     <><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></>,
        prog:     <><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></>,
        gender:   <><circle cx="12" cy="8" r="5"/><path d="M20 21a8 8 0 1 0-16 0"/></>,
        level:    <><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></>,
        year:     <><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>,
    };
    return <svg xmlns="http://www.w3.org/2000/svg" {...s}>{icons[type]}</svg>;
}

// ─── Chart Components ─────────────────────────────────────────────────────────

function HorizontalBarChart({ data, height, colorKey = 0 }) {
    const sorted = [...data].sort((a, b) => b.count - a.count).slice(0, 20);
    const h = Math.max(height ?? 300, sorted.length * 44 + 80);
    return (
        <ApexChart type="bar" height={h}
            series={[{ name: 'Students', data: sorted.map(d => d.count) }]}
            options={{
                chart: { ...BASE, type: 'bar' },
                plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '62%', distributed: true } },
                dataLabels: {
                    enabled: true, offsetX: 6,
                    style: { fontSize: '10px', colors: ['#354052'] },
                    formatter: v => v.toLocaleString(),
                },
                xaxis: {
                    categories: sorted.map(d => d.label || 'N/A'),
                    labels: { style: { fontSize: '10px' } },
                },
                yaxis: { labels: { style: { fontSize: '10px' }, maxWidth: 220 } },
                colors: C.palette,
                legend: { show: false },
                tooltip: { y: { formatter: v => `${v.toLocaleString()} students` } },
                grid: { borderColor: '#f0f0f0', strokeDashArray: 3 },
            }} />
    );
}

function VerticalBarChart({ data, height = 280, color }) {
    return (
        <ApexChart type="bar" height={height}
            series={[{ name: 'Students', data: data.map(d => d.count) }]}
            options={{
                chart: { ...BASE, type: 'bar' },
                plotOptions: { bar: { borderRadius: 4, columnWidth: '55%', distributed: true } },
                dataLabels: { enabled: false },
                xaxis: { categories: data.map(d => d.label || 'N/A'), labels: { style: { fontSize: '10px' } } },
                yaxis: { labels: { formatter: v => Math.round(v).toLocaleString() } },
                colors: color ? [color] : C.palette,
                legend: { show: false },
                tooltip: { y: { formatter: v => `${v.toLocaleString()} students` } },
                grid: { borderColor: '#f0f0f0', strokeDashArray: 3 },
            }} />
    );
}

function DonutChart({ data, height = 300 }) {
    const labels = data.map(d => d.label || 'N/A');
    const values = data.map(d => d.count);
    const total  = values.reduce((a, b) => a + b, 0);
    return (
        <ApexChart type="donut" height={height} series={values} options={{
            chart: { ...BASE, type: 'donut' },
            labels,
            colors: C.palette,
            legend: { position: 'bottom', fontSize: '12px' },
            dataLabels: { formatter: val => `${val.toFixed(1)}%` },
            plotOptions: {
                pie: {
                    donut: {
                        size: '64%',
                        labels: {
                            show: true,
                            total: {
                                show: true, label: 'Total', fontSize: '12px', fontWeight: 600,
                                formatter: () => total.toLocaleString(),
                            },
                            value: { fontSize: '20px', fontWeight: 700, formatter: v => Number(v).toLocaleString() },
                        },
                    },
                },
            },
            tooltip: { y: { formatter: v => `${v.toLocaleString()} students` } },
        }} />
    );
}

function PieChart({ data, height = 300 }) {
    const labels = data.map(d => d.label || 'N/A');
    const values = data.map(d => d.count);
    return (
        <ApexChart type="pie" height={height} series={values} options={{
            chart: { ...BASE, type: 'pie' },
            labels,
            colors: [C.primary, C.danger, C.purple, C.success, C.warning, C.info],
            legend: { position: 'bottom', fontSize: '12px' },
            dataLabels: { formatter: (val, opts) => `${opts.w.globals.labels[opts.seriesIndex]}: ${val.toFixed(1)}%` },
            tooltip: { y: { formatter: v => `${v.toLocaleString()} students` } },
        }} />
    );
}

function AreaTrendChart({ data, height = 260 }) {
    return (
        <ApexChart type="area" height={height}
            series={[{ name: 'Students', data: data.map(d => d.count) }]}
            options={{
                chart: { ...BASE, type: 'area' },
                stroke: { curve: 'smooth', width: 2.5 },
                fill: { type: 'gradient', gradient: { opacityFrom: 0.40, opacityTo: 0.03 } },
                dataLabels: { enabled: false },
                xaxis: { categories: data.map(d => d.label), labels: { style: { fontSize: '11px' } } },
                yaxis: { labels: { formatter: v => Math.round(v).toLocaleString() } },
                colors: [C.primary],
                markers: { size: 4, strokeWidth: 0, hover: { size: 6 } },
                tooltip: { y: { formatter: v => `${v.toLocaleString()} students` } },
                grid: { borderColor: '#f0f0f0', strokeDashArray: 3 },
            }} />
    );
}

// ─── Breakdown Table ──────────────────────────────────────────────────────────

function BreakdownTable({ rows, total }) {
    const grand = rows.reduce((s, r) => s + r.count, 0);
    return (
        <div className="table-responsive">
            <table className="table table-vcenter table-striped table-sm mb-0">
                <thead className="table-light">
                    <tr>
                        <th style={{ width: 40 }}>#</th>
                        <th>Group</th>
                        <th className="text-end">Students</th>
                        <th className="text-end">Share</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0
                        ? <tr><td colSpan={4} className="text-center text-muted py-4">No data available</td></tr>
                        : rows.map((r, i) => (
                            <tr key={i}>
                                <td className="text-muted">{i + 1}</td>
                                <td><strong>{r.label || 'N/A'}</strong></td>
                                <td className="text-end fw-bold" style={{ color: C.primary }}>{r.count.toLocaleString()}</td>
                                <td className="text-end">
                                    <span className="badge" style={{ background: `${C.primary}18`, color: C.primary, fontSize: '11px' }}>
                                        {pct(r.count, total)}
                                    </span>
                                </td>
                            </tr>
                        ))
                    }
                </tbody>
                {rows.length > 0 && (
                    <tfoot className="table-light fw-bold">
                        <tr>
                            <td colSpan={2}>Total</td>
                            <td className="text-end" style={{ color: C.success }}>{grand.toLocaleString()}</td>
                            <td className="text-end">100%</td>
                        </tr>
                    </tfoot>
                )}
            </table>
        </div>
    );
}

// ─── Card Wrapper ─────────────────────────────────────────────────────────────

function CC({ title, subtitle, col, children }) {
    return (
        <div className={col}>
            <div className="card h-100">
                <div className="card-header">
                    <div>
                        <h3 className="card-title mb-0">{title}</h3>
                        {subtitle && <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.75rem' }}>{subtitle}</p>}
                    </div>
                </div>
                <div className="card-body">{children}</div>
            </div>
        </div>
    );
}

// ─── Printable Report ─────────────────────────────────────────────────────────

const PrintableReport = React.forwardRef(({ kpis, breakdowns, filters }, ref) => {
    const { totalStudents, totalDepts, totalProgs, maleCount, femaleCount } = kpis;
    const date = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });

    const activeFilters = [];
    if (filters.filterDept    !== 'All') activeFilters.push(`Dept: ${filters.filterDept}`);
    if (filters.filterProg    !== 'All') activeFilters.push(`Prog: ${filters.filterProg}`);
    if (filters.filterGender  !== 'All') activeFilters.push(`Gender: ${filters.filterGender}`);
    if (filters.filterType    !== 'All') activeFilters.push(`Study Type: ${filters.filterType}`);
    if (filters.filterYear    !== 'All') activeFilters.push(`Year: ${filters.filterYear}`);

    const SectionTable = ({ title, rows }) => {
        const grand = rows.reduce((s, r) => s + r.count, 0);
        return (
            <div style={{ marginBottom: 16 }}>
                <h2>{title}</h2>
                <table>
                    <thead>
                        <tr>
                            <th>#</th><th>Group</th>
                            <th>Students</th><th>Share</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i}>
                                <td>{i + 1}</td>
                                <td>{r.label || 'N/A'}</td>
                                <td>{r.count.toLocaleString()}</td>
                                <td>{pct(r.count, grand)}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={2}>Total</td>
                            <td>{grand.toLocaleString()}</td>
                            <td>100%</td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        );
    };

    return (
        <div ref={ref} style={{ padding: '6mm', fontFamily: 'Arial, sans-serif', width: '100%' }}>
            <div className="report-header-bar" style={{ textAlign: 'center' }}>
                <h1 style={{ margin: 0, color: '#fff' }}>ODFeL — Kaduna Polytechnic</h1>
                <p style={{ margin: '4px 0 0', color: '#e0ecff', fontSize: '12px' }}>
                    Active Student Analytics Report &nbsp;|&nbsp; Generated: {date}
                    {activeFilters.length > 0 && <> &nbsp;|&nbsp; Filters: {activeFilters.join(', ')}</>}
                </p>
            </div>

            <div className="kpi-row">
                {[
                    ['Total Active Students', totalStudents.toLocaleString()],
                    ['Departments',           totalDepts.toLocaleString()],
                    ['Programmes',            totalProgs.toLocaleString()],
                    ['Male Students',         maleCount.toLocaleString()],
                    ['Female Students',       femaleCount.toLocaleString()],
                ].map(([lbl, val]) => (
                    <div key={lbl} className="kpi-box">
                        <div className="kpi-lbl">{lbl}</div>
                        <div className="kpi-val">{val}</div>
                    </div>
                ))}
            </div>

            <SectionTable title="Breakdown by Level"       rows={breakdowns.level}      />
            <SectionTable title="Breakdown by Department"  rows={breakdowns.department}  />
            <SectionTable title="Breakdown by Programme"   rows={breakdowns.programme}   />
            <SectionTable title="Breakdown by Gender"      rows={breakdowns.gender}      />
            <SectionTable title="Breakdown by Study Type"  rows={breakdowns.study_type}  />
            <SectionTable title="Breakdown by Adm. Year"   rows={breakdowns.year}        />

            <h2 style={{ marginBottom: 12 }}>Chart Trends</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
                <div className="chart-card">
                    <div className="chart-card-title">Students by Level</div>
                    <VerticalBarChart data={breakdowns.level} height={220} />
                </div>
                <div className="chart-card">
                    <div className="chart-card-title">Gender Distribution</div>
                    <PieChart data={breakdowns.gender} height={220} />
                </div>
            </div>
            <div className="chart-card">
                <div className="chart-card-title">Students by Department</div>
                <HorizontalBarChart data={breakdowns.department} height={260} />
            </div>
        </div>
    );
});
PrintableReport.displayName = 'PrintableReport';

// ─── Helper: group students by a key ─────────────────────────────────────────

function groupBy(data, keyFn) {
    const g = {};
    data.forEach(s => {
        const k = keyFn(s) || 'N/A';
        g[k] = (g[k] || 0) + 1;
    });
    return Object.entries(g)
        .map(([label, count]) => ({ label, count }))
        .sort((a, b) => b.count - a.count);
}

// ─── Resolve Level directly from STUDENT_LEVEL column ────────────────────────

function resolveLevel(s) {
    const lvl = s.STUDENT_LEVEL;
    if (lvl !== undefined && lvl !== null && String(lvl).trim() !== '') {
        return String(lvl).trim();
    }
    return 'N/A';
}

// ─── Main Component ───────────────────────────────────────────────────────────

function ActiveStudentAnalyticsDashboard() {
    const [isLoading,     setIsLoading]    = useState(true);
    const [rawStudents,   setRawStudents]  = useState([]);
    const [departments,   setDepartments]  = useState([]);
    const [programmes,    setProgrammes]   = useState([]);

    // Filters
    const [filterDept,   setFilterDept]   = useState('All');
    const [filterProg,   setFilterProg]   = useState('All');
    const [filterGender, setFilterGender] = useState('All');
    const [filterType,   setFilterType]   = useState('All');
    const [filterYear,   setFilterYear]   = useState('All');
    const [activeTab,    setActiveTab]    = useState('level');

    const printRef = useRef(null);

    useEffect(() => { getData(); }, []);

    const getData = async () => {
        try {
            const result = await axios.get(`${serverLink}staff/report/dashboard/data`);
            const data = result.data || {};
            setRawStudents(data.ActiveStudents || []);
            setDepartments(data.Department    || []);
            setProgrammes(data.Programme      || []);
        } catch (e) {
            console.log("NETWORK ERROR", e);
        } finally {
            setIsLoading(false);
        }
    };

    // ─── Filter options ──────────────────────────────────────────────────────

    const allDepts = useMemo(() => {
        const s = new Set(rawStudents.map(s => s.DEPARTMENT).filter(Boolean));
        return [...s].sort();
    }, [rawStudents]);

    const allProgs = useMemo(() => {
        const s = new Set(rawStudents.map(s => s.PROGRAMME).filter(Boolean));
        return [...s].sort();
    }, [rawStudents]);

    const allGenders = useMemo(() => {
        const s = new Set(rawStudents.map(s => s.GENDER).filter(Boolean));
        return [...s].sort();
    }, [rawStudents]);

    const allTypes = useMemo(() => {
        const s = new Set(rawStudents.map(s => s.STUDY_TYPE).filter(Boolean));
        return [...s].sort();
    }, [rawStudents]);

    const allYears = useMemo(() => {
        const s = new Set(rawStudents.map(s => s.ADMISSION_YEAR).filter(Boolean));
        return [...s].sort((a, b) => Number(a) - Number(b)).map(String);
    }, [rawStudents]);

    // ─── Filtered records ────────────────────────────────────────────────────

    const filtered = useMemo(() => rawStudents.filter(s => {
        if (filterDept   !== 'All' && s.DEPARTMENT !== filterDept)   return false;
        if (filterProg   !== 'All' && s.PROGRAMME  !== filterProg)   return false;
        if (filterGender !== 'All' && s.GENDER     !== filterGender) return false;
        if (filterType   !== 'All' && s.STUDY_TYPE !== filterType)   return false;
        if (filterYear   !== 'All' && String(s.ADMISSION_YEAR) !== filterYear) return false;
        return true;
    }), [rawStudents, filterDept, filterProg, filterGender, filterType, filterYear]);

    // ─── KPIs ────────────────────────────────────────────────────────────────

    const totalStudents = filtered.length;
    const totalDepts    = useMemo(() => new Set(filtered.map(s => s.DEPARTMENT).filter(Boolean)).size, [filtered]);
    const totalProgs    = useMemo(() => new Set(filtered.map(s => s.PROGRAMME).filter(Boolean)).size, [filtered]);
    const maleCount     = useMemo(() => filtered.filter(s => /male/i.test(s.GENDER) && !/female/i.test(s.GENDER)).length, [filtered]);
    const femaleCount   = useMemo(() => filtered.filter(s => /female/i.test(s.GENDER)).length, [filtered]);

    // ─── Breakdowns ──────────────────────────────────────────────────────────

    const breakdowns = useMemo(() => ({
        level:      groupBy(filtered, s => resolveLevel(s)),
        department: groupBy(filtered, s => s.DEPARTMENT),
        programme:  groupBy(filtered, s => s.PROGRAMME),
        gender:     groupBy(filtered, s => s.GENDER),
        study_type: groupBy(filtered, s => s.STUDY_TYPE),
        year:       groupBy(filtered, s => String(s.ADMISSION_YEAR)).sort((a, b) => Number(a.label) - Number(b.label)),
    }), [filtered]);

    const currentBD = breakdowns[activeTab] || [];
    const hasFilter = filterDept !== 'All' || filterProg !== 'All' || filterGender !== 'All'
                   || filterType !== 'All' || filterYear !== 'All';

    // ─── Print ───────────────────────────────────────────────────────────────

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: `Active-Student-Analytics-${new Date().toISOString().split('T')[0]}`,
        pageStyle: `
            @page { size: A4 landscape; margin: 10mm; }
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            body { font-family: Arial, Helvetica, sans-serif; font-size: 12px; color: #1a1a2e; }
            h1  { font-size: 20px; margin: 0 0 4px; }
            h2  { font-size: 14px; margin: 18px 0 7px; color: #206bc4; border-bottom: 2px solid #206bc4; padding-bottom: 4px; }
            p   { font-size: 11px; margin: 0 0 10px; color: #555; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 6px; }
            thead th { background:#206bc4 !important; color:#fff !important; padding:5px 7px; text-align:left; font-size:11px; white-space:nowrap; }
            tbody td { padding:4px 7px; border-bottom:1px solid #e9ecef; font-size:11px; }
            tbody tr:nth-child(even) td { background:#f0f4ff !important; }
            tfoot td { padding:4px 7px; font-weight:700; background:#dce7ff !important; font-size:11px; }
            .kpi-row { display:flex; gap:10px; margin-bottom:14px; }
            .kpi-box { flex:1; border:1px solid #206bc4; border-radius:6px; padding:10px; text-align:center; background:#f0f4ff !important; }
            .kpi-val { font-size:18px; font-weight:700; color:#206bc4 !important; }
            .kpi-lbl { font-size:10px; text-transform:uppercase; color:#555; letter-spacing:.05em; font-weight:600; }
            .chart-card { border:1px solid #dce7ff; border-radius:6px; padding:10px; margin-bottom:14px; background:#f8faff !important; }
            .chart-card-title { font-size:13px; font-weight:700; color:#206bc4; margin-bottom:6px; }
            .report-header-bar { background:#206bc4 !important; color:#fff !important; padding:12px 16px; border-radius:6px; margin-bottom:14px; }
        `,
    });

    // ─── Export Excel ─────────────────────────────────────────────────────────

    const exportExcel = () => {
        const date  = new Date().toLocaleDateString('en-GB');
        const thS   = 'background:#206bc4;color:#fff;padding:5px 8px;font-weight:bold;border:1px solid #1a56a0;';
        const tdS   = 'padding:4px 8px;border:1px solid #dee2e6;';
        const tdAS  = 'padding:4px 8px;border:1px solid #dee2e6;background:#f8f9fc;';
        const hdS   = 'font-size:13px;font-weight:bold;color:#206bc4;padding:8px 4px;';

        const mkTbl = (title, rows) => {
            const total   = rows.reduce((s, r) => s + r.count, 0);
            const headers = ['#', 'Group', 'Students', 'Share'];
            const head    = `<tr>${headers.map(h => `<th style="${thS}">${h}</th>`).join('')}</tr>`;
            const body    = rows.map((r, i) => `<tr>
                <td style="${i % 2 === 0 ? tdS : tdAS}">${i + 1}</td>
                <td style="${i % 2 === 0 ? tdS : tdAS}">${r.label || 'N/A'}</td>
                <td style="${i % 2 === 0 ? tdS : tdAS}">${r.count.toLocaleString()}</td>
                <td style="${i % 2 === 0 ? tdS : tdAS}">${pct(r.count, total)}</td>
            </tr>`).join('');
            const foot    = `<tr>
                <td style="font-weight:bold;${tdS}" colspan="2">Total</td>
                <td style="font-weight:bold;${tdS}">${total.toLocaleString()}</td>
                <td style="font-weight:bold;${tdS}">100%</td>
            </tr>`;
            return `
                <tr><td colspan="4" style="${hdS}">${title}</td></tr>
                <table border="0" style="width:100%;border-collapse:collapse;">
                    <thead>${head}</thead><tbody>${body}</tbody><tfoot>${foot}</tfoot>
                </table>
                <tr><td>&nbsp;</td></tr>`;
        };

        const html = `
        <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
        <head><meta charset="utf-8"/></head>
        <body>
        <table style="font-family:Arial;font-size:10px;width:100%;">
            <tr><td colspan="4" style="font-size:16px;font-weight:bold;padding:6px 4px;">ODFeL — Active Student Analytics Dashboard</td></tr>
            <tr><td colspan="4" style="color:#555;padding:2px 4px;">Generated: ${date} | Total Students: ${totalStudents.toLocaleString()}</td></tr>
            <tr><td>&nbsp;</td></tr>
            ${mkTbl('BREAKDOWN BY LEVEL',      breakdowns.level)}
            ${mkTbl('BREAKDOWN BY DEPARTMENT', breakdowns.department)}
            ${mkTbl('BREAKDOWN BY PROGRAMME',  breakdowns.programme)}
            ${mkTbl('BREAKDOWN BY GENDER',     breakdowns.gender)}
            ${mkTbl('BREAKDOWN BY STUDY TYPE', breakdowns.study_type)}
            ${mkTbl('BREAKDOWN BY ADM. YEAR',  breakdowns.year)}
        </table>
        </body></html>`;

        const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = `Active-Student-Analytics-${new Date().toISOString().split('T')[0]}.xls`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // ─── Loading ──────────────────────────────────────────────────────────────

    if (isLoading) {
        return (
            <div className="container-xl" style={{ height: '700px' }}>
                <div className="page-header d-print-none"><DataLoader /></div>
            </div>
        );
    }

    // ─── Render ───────────────────────────────────────────────────────────────

    return (
        <>
            {/* ── Hidden Printable ─────────────────────────────────────────── */}
            <div style={{ display: 'none' }}>
                <PrintableReport
                    ref={printRef}
                    kpis={{ totalStudents, totalDepts, totalProgs, maleCount, femaleCount }}
                    breakdowns={breakdowns}
                    filters={{ filterDept, filterProg, filterGender, filterType, filterYear }}
                />
            </div>

            {/* ── Page Header ──────────────────────────────────────────────── */}
            <div className="container-xl">
                <div className="page-header d-print-none">
                    <div className="row align-items-center">
                        <div className="col">
                            <div className="page-pretitle">Registration / Reports</div>
                            <h2 className="page-title">Active Student Analytics Dashboard</h2>
                        </div>
                        <div className="col-auto ms-auto d-print-none">
                            <div className="btn-list">
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon me-1" width="18" height="18"
                                        viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none"
                                        strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                        <path d="M17 17h2a2 2 0 0 0 2-2v-4a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2"/>
                                        <path d="M17 9V5a2 2 0 0 0-2-2H9a2 2 0 0 0-2 2v4"/>
                                        <rect x="7" y="13" width="10" height="8" rx="2"/>
                                    </svg>
                                    Print Report
                                </button>
                                <button className="btn btn-success" onClick={exportExcel}>
                                    <svg xmlns="http://www.w3.org/2000/svg" className="icon me-1" width="18" height="18"
                                        viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none"
                                        strokeLinecap="round" strokeLinejoin="round">
                                        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                        <path d="M14 3v4a1 1 0 0 0 1 1h4"/>
                                        <path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z"/>
                                        <path d="M8 11h8"/><path d="M8 15h5"/><path d="M11 19v-8"/>
                                    </svg>
                                    Export Excel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="page-body">
                <div className="container-xl">

                    {/* ── Filter Bar ────────────────────────────────────────── */}
                    <div className="card mb-3" style={{ border: '1.5px solid #d0e2f7', background: '#f8fbff' }}>
                        <div className="card-body py-3">
                            <div className="row g-2 align-items-end">

                                {/* Department */}
                                <div className="col-auto">
                                    <label className="form-label mb-1"
                                        style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b' }}>
                                        Department
                                    </label>
                                    <select className="form-select form-select-sm" value={filterDept}
                                        onChange={e => setFilterDept(e.target.value)} style={{ minWidth: 180 }}>
                                        <option value="All">All Departments</option>
                                        {allDepts.map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                </div>

                                {/* Programme */}
                                <div className="col-auto">
                                    <label className="form-label mb-1"
                                        style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b' }}>
                                        Programme
                                    </label>
                                    <select className="form-select form-select-sm" value={filterProg}
                                        onChange={e => setFilterProg(e.target.value)} style={{ minWidth: 220 }}>
                                        <option value="All">All Programmes</option>
                                        {allProgs.map(p => <option key={p} value={p}>{p}</option>)}
                                    </select>
                                </div>

                                {/* Gender */}
                                <div className="col-auto">
                                    <label className="form-label mb-1"
                                        style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b' }}>
                                        Gender
                                    </label>
                                    <select className="form-select form-select-sm" value={filterGender}
                                        onChange={e => setFilterGender(e.target.value)} style={{ minWidth: 130 }}>
                                        <option value="All">All Genders</option>
                                        {allGenders.map(g => <option key={g} value={g}>{g}</option>)}
                                    </select>
                                </div>

                                {/* Study Type */}
                                <div className="col-auto">
                                    <label className="form-label mb-1"
                                        style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b' }}>
                                        Study Type
                                    </label>
                                    <select className="form-select form-select-sm" value={filterType}
                                        onChange={e => setFilterType(e.target.value)} style={{ minWidth: 130 }}>
                                        <option value="All">All Types</option>
                                        {allTypes.map(t => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>

                                {/* Admission Year */}
                                <div className="col-auto">
                                    <label className="form-label mb-1"
                                        style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b' }}>
                                        Adm. Year
                                    </label>
                                    <select className="form-select form-select-sm" value={filterYear}
                                        onChange={e => setFilterYear(e.target.value)} style={{ minWidth: 120 }}>
                                        <option value="All">All Years</option>
                                        {allYears.map(y => <option key={y} value={y}>{y}</option>)}
                                    </select>
                                </div>

                                {/* Clear Filters */}
                                {hasFilter && (
                                    <div className="col-auto">
                                        <button className="btn btn-sm btn-outline-danger"
                                            onClick={() => {
                                                setFilterDept('All'); setFilterProg('All');
                                                setFilterGender('All'); setFilterType('All'); setFilterYear('All');
                                            }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                                                viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none"
                                                strokeLinecap="round" strokeLinejoin="round" className="me-1">
                                                <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                                                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                                            </svg>
                                            Clear Filters
                                        </button>
                                    </div>
                                )}

                                <div className="col ms-auto text-end">
                                    <span className="text-muted" style={{ fontSize: '0.76rem' }}>
                                        Showing <strong>{totalStudents.toLocaleString()}</strong> of{' '}
                                        <strong>{rawStudents.length.toLocaleString()}</strong> students
                                        {hasFilter && (
                                            <span className="ms-2 badge"
                                                style={{ background: `${C.primary}18`, color: C.primary, fontSize: '10px' }}>
                                                Filtered
                                            </span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── KPI Cards ─────────────────────────────────────────── */}
                    <div className="row row-deck row-cards mb-3">
                        {[
                            { label: 'Total Active Students', val: totalStudents.toLocaleString(),          icon: 'students', accent: C.primary },
                            { label: 'Departments',           val: totalDepts.toLocaleString(),             icon: 'dept',     accent: C.success },
                            { label: 'Programmes',            val: totalProgs.toLocaleString(),             icon: 'prog',     accent: C.purple  },
                            { label: 'Male Students',         val: maleCount.toLocaleString(),              icon: 'gender',   accent: C.info    },
                            { label: 'Female Students',       val: femaleCount.toLocaleString(),            icon: 'gender',   accent: C.warning },
                            { label: 'Gender Ratio (M:F)',
                              val: femaleCount > 0
                                    ? `${(maleCount / femaleCount).toFixed(1)}:1`
                                    : (maleCount > 0 ? '∞:1' : '—'),
                              icon: 'level', accent: C.danger },
                        ].map(({ label, val, icon, accent }) => (
                            <div key={label} className="col-6 col-lg">
                                <div className="card" style={{ borderTop: `3px solid ${accent}` }}>
                                    <div className="card-body p-3">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <span style={{
                                                width: 34, height: 34, borderRadius: 9,
                                                background: `${accent}18`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                                            }}>
                                                <KpiIcon type={icon} color={accent} />
                                            </span>
                                            <span style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '.05em', color: '#64748b', lineHeight: 1.3 }}>
                                                {label}
                                            </span>
                                        </div>
                                        <div className="fw-bold" style={{ fontSize: '1.45rem', wordBreak: 'break-word', color: accent }}>{val}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* ── Summary Banner ────────────────────────────────────── */}
                    <div className="alert alert-info d-flex align-items-center mb-3" role="alert">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                            strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round"
                            strokeLinejoin="round" className="me-2" style={{ flexShrink: 0 }}>
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
                            <circle cx="12" cy="12" r="9"/>
                            <line x1="12" y1="8" x2="12.01" y2="8"/>
                            <polyline points="11 12 12 12 12 16 13 16"/>
                        </svg>
                        <div style={{ fontSize: '0.85rem' }}>
                            <strong>{totalStudents.toLocaleString()}</strong> active students enrolled across{' '}
                            <strong>{totalDepts}</strong> department{totalDepts !== 1 ? 's' : ''} and{' '}
                            <strong>{totalProgs}</strong> programme{totalProgs !== 1 ? 's' : ''}.{' '}
                            Male: <strong>{maleCount.toLocaleString()}</strong> &mdash; Female: <strong>{femaleCount.toLocaleString()}</strong>.
                            {hasFilter && <span className="ms-2 badge bg-warning text-dark">Filtered View Active</span>}
                        </div>
                    </div>

                    {/* ── Charts Row 1: Level + Gender ──────────────────────── */}
                    <div className="row row-deck row-cards mb-3">
                        <CC title="Students by Level" subtitle="Distribution across academic levels"
                            col="col-lg-7">
                            <VerticalBarChart data={breakdowns.level} height={280} />
                        </CC>
                        <CC title="Gender Distribution" subtitle="Male vs Female breakdown"
                            col="col-lg-5">
                            <PieChart data={breakdowns.gender} height={280} />
                        </CC>
                    </div>

                    {/* ── Charts Row 2: Study Type + Admission Year Trend ───── */}
                    <div className="row row-deck row-cards mb-3">
                        <CC title="Study Type Distribution" subtitle="ND vs HND breakdown"
                            col="col-lg-5">
                            <DonutChart data={breakdowns.study_type} height={280} />
                        </CC>
                        <CC title="Admission Year Trend" subtitle="Students enrolled per admission year"
                            col="col-lg-7">
                            <AreaTrendChart data={[...breakdowns.year].sort((a, b) => Number(a.label) - Number(b.label))} height={280} />
                        </CC>
                    </div>

                    {/* ── Department Bar ────────────────────────────────────── */}
                    <div className="row row-deck row-cards mb-3">
                        <CC title="Students by Department" subtitle="Top departments by active student count"
                            col="col-12">
                            <HorizontalBarChart data={breakdowns.department} />
                        </CC>
                    </div>

                    {/* ── Programme Bar ─────────────────────────────────────── */}
                    <div className="row row-deck row-cards mb-3">
                        <CC title="Students by Programme" subtitle="Top programmes by active student count"
                            col="col-12">
                            <HorizontalBarChart data={breakdowns.programme} />
                        </CC>
                    </div>

                    {/* ── Table Tabs ────────────────────────────────────────── */}
                    <div style={{ background: '#f0f4fd', borderRadius: '14px', padding: '7px', marginBottom: '16px', overflowX: 'auto' }}>
                        <div style={{ display: 'flex', gap: '4px', minWidth: 'max-content' }}>
                            {TABS.map(tab => {
                                const active = activeTab === tab.key;
                                const count  = (breakdowns[tab.key] || []).length;
                                return (
                                    <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '7px',
                                            padding: '9px 15px', border: 'none', borderRadius: '10px',
                                            cursor: 'pointer', fontSize: '13px',
                                            fontWeight: active ? 600 : 400,
                                            background: active ? C.primary : 'transparent',
                                            color: active ? '#fff' : '#64748b',
                                            transition: 'background 0.15s, color 0.15s',
                                            whiteSpace: 'nowrap',
                                        }}>
                                        {tab.label}
                                        <span style={{
                                            fontSize: '10px', fontWeight: 700,
                                            padding: '1px 7px', borderRadius: '20px',
                                            background: active ? 'rgba(255,255,255,0.22)' : '#dde3f0',
                                            color: active ? '#fff' : '#64748b',
                                            minWidth: '20px', textAlign: 'center',
                                        }}>
                                            {count}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* ── Breakdown Table ───────────────────────────────────── */}
                    <div className="card mb-4">
                        <div className="card-header">
                            <h3 className="card-title mb-0">
                                {TABS.find(t => t.key === activeTab)?.label} — Detailed Breakdown
                            </h3>
                            <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.75rem' }}>
                                {currentBD.length} group{currentBD.length !== 1 ? 's' : ''} · {totalStudents.toLocaleString()} total students
                            </p>
                        </div>
                        <div className="card-body p-0">
                            <BreakdownTable rows={currentBD} total={totalStudents} />
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
}

const mapStateToProps = (state) => ({
    loginData: state.loginDetails,
});

export default connect(mapStateToProps, null)(ActiveStudentAnalyticsDashboard);
